import React, { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import { countryCodes, getDialCode } from '../data/countryCodes';

const EMAILJS_CONFIG = {
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_hkhj1k8',
    templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_freg9in',
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'XqjsT_gI4D6cMu3oD',
    recipient: process.env.REACT_APP_CONTACT_EMAIL || 'info@cloudmindx.com'
};

const initialForm = {
    name: '',
    email: '',
    countryIso: 'UAE',
    phone: '',
    message: ''
};

const ContactPopup = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [status, setStatus] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (sessionStorage.getItem('cloudmindx_contact_popup_seen')) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setIsOpen(true);
            sessionStorage.setItem('cloudmindx_contact_popup_seen', 'true');
        }, 5000);

        return () => window.clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const handleChange = (event) => {
        setStatus('');
        setFormData((current) => ({
            ...current,
            [event.target.name]: event.target.value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        setStatus('sending');

        const cleanData = Object.fromEntries(
            Object.entries(formData).map(([key, value]) => [key, value.trim()])
        );

        try {
            await emailjs.send(
                EMAILJS_CONFIG.serviceId,
                EMAILJS_CONFIG.templateId,
                {
                    to_email: EMAILJS_CONFIG.recipient,
                    to_name: 'Cloud Mind X Team',
                    from_name: cleanData.name,
                    first_name: cleanData.name,
                    last_name: '',
                    from_email: cleanData.email,
                    reply_to: cleanData.email,
                    phone: `${getDialCode(cleanData.countryIso)} ${cleanData.phone}`,
                    message: cleanData.message,
                    subject: `New website enquiry from ${cleanData.name}`,
                    submitted_at: new Date().toLocaleString('en-IN', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                        timeZone: 'Asia/Kolkata'
                    })
                },
                { publicKey: EMAILJS_CONFIG.publicKey }
            );

            setStatus('success');
            setFormData(initialForm);
            window.setTimeout(() => setIsOpen(false), 1800);
        } catch (error) {
            console.error('Contact popup email failed:', error);
            setStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div
            className="contact-popup-overlay"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    setIsOpen(false);
                }
            }}
        >
            <section
                className="contact-popup"
                role="dialog"
                aria-modal="true"
                aria-labelledby="contact-popup-title"
            >
                <button
                    type="button"
                    className="contact-popup-close"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close contact form"
                >
                    ×
                </button>

                <div className="contact-popup-intro">
                    <span>LET'S BUILD SOMETHING GREAT</span>
                    <h2 id="contact-popup-title">Have a project in mind?</h2>
                    <p>Share a few details and our team will get back to you within 24 hours.</p>
                    <div className="contact-popup-benefit">✓ Free initial consultation</div>
                    <div className="contact-popup-benefit">✓ No-obligation project estimate</div>
                </div>

                <form className="contact-popup-form" onSubmit={handleSubmit}>
                    <div className="contact-popup-field">
                        <label htmlFor="popup-name">Full name</label>
                        <input
                            id="popup-name"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            autoComplete="name"
                            maxLength="120"
                            placeholder="Your name"
                            autoFocus
                            required
                        />
                    </div>

                    <div className="contact-popup-grid">
                        <div className="contact-popup-field">
                            <label htmlFor="popup-email">Email address</label>
                            <input
                                id="popup-email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                autoComplete="email"
                                maxLength="254"
                                placeholder="name@company.com"
                                required
                            />
                        </div>
                        <div className="contact-popup-field">
                            <label htmlFor="popup-phone">Phone number</label>
                            <div className="country-phone-control">
                                <select
                                    name="countryIso"
                                    value={formData.countryIso}
                                    onChange={handleChange}
                                    aria-label="Country"
                                >
                                    {countryCodes.map(({ iso }) => (
                                        <option key={iso} value={iso}>
                                            {iso}
                                        </option>
                                    ))}
                                </select>
                                <span className="country-dial-code">
                                    {getDialCode(formData.countryIso)}
                                </span>
                                <input
                                    id="popup-phone"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    autoComplete="tel-national"
                                    inputMode="tel"
                                    pattern="[0-9 ()-]{6,20}"
                                    maxLength="20"
                                    placeholder="Phone number"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <div className="contact-popup-field">
                        <label htmlFor="popup-message">How can we help?</label>
                        <textarea
                            id="popup-message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            minLength="10"
                            maxLength="2000"
                            rows="3"
                            placeholder="Tell us briefly about your project"
                            required
                        />
                    </div>

                    <div className="contact-popup-status" aria-live="polite" role="status">
                        {status === 'success' && 'Thank you! Your message has been sent successfully.'}
                        {status === 'error' && 'Message could not be sent. Please try again.'}
                    </div>

                    <button type="submit" className="contact-popup-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send enquiry'}
                        {!isSubmitting && <span aria-hidden="true">→</span>}
                    </button>
                </form>
            </section>
        </div>
    );
};

export default ContactPopup;
